import React, { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import {
  useBetsByPersonaQuery,
  useAllBetsQuery,
  useBettingStatsQuery,
} from "@/api/betting";
import { usePersonasQueryNoPublic } from "@/api/personas";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import {
  ChevronsUpDown,
  Check,
  RefreshCw,
  Award,
  Database,
  Loader2,
  LayoutDashboard,
  BarChart,
} from "lucide-react";

// Import our betting dashboard components
import BetsDisplay from "@/components/betting/BetsDisplay";
import BettingStats from "@/components/betting/BettingStats";
import BettingInsights from "@/components/betting/BettingInsights";
import JobsPanel from "@/components/betting/JobsPanel";
import ValidateModal from "@/components/betting/modals/ValidateModal";
import BackfillModal from "@/components/betting/modals/BackfillModal";

const BettingDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [viewMode, setViewMode] = useState<"cards" | "table">("cards");
  const [dashboardView, setDashboardView] = useState<"standard" | "analytics">(
    "standard",
  );
  const { toast } = useToast();
  const [personaOpen, setPersonaOpen] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState<string>("");
  const [showValidateModal, setShowValidateModal] = useState(false);
  const [showBackfillModal, setShowBackfillModal] = useState(false);

  // Fetch personas data
  const { data: personas = [], isError: isPersonasError } =
    usePersonasQueryNoPublic();
  console.error("Personas Error: ", isPersonasError);

  // Fetch bets data for selected persona or all personas
  const personaBetsQuery = useBetsByPersonaQuery(
    selectedPersona,
    2000, // Increased limit to get more data for analytics
    activeTab === "all" ? undefined : activeTab,
    undefined,
    !!selectedPersona,
  );

  const allBetsQuery = useAllBetsQuery(
    2000, // Increased limit
    activeTab === "all" ? undefined : activeTab,
    undefined,
    !selectedPersona,
  );

  // Determine which query to use based on persona selection
  const betsData = selectedPersona ? personaBetsQuery.data : allBetsQuery.data;
  const isBetsLoading = selectedPersona
    ? personaBetsQuery.isLoading
    : allBetsQuery.isLoading;
  const isBetsError = selectedPersona
    ? personaBetsQuery.isError
    : allBetsQuery.isError;
  const betsError = selectedPersona
    ? personaBetsQuery.error
    : allBetsQuery.error;

  // Get betting stats from API for the selected persona
  const { data: statsData } = useBettingStatsQuery(selectedPersona);

  // Refetch bets data
  const refetchBets = () => {
    if (selectedPersona) {
      personaBetsQuery.refetch();
    } else {
      allBetsQuery.refetch();
    }
  };

  const handleRefresh = () => {
    refetchBets();
    toast({
      description: "Refreshing betting data...",
    });
  };

  const handlePersonaChange = (value: string) => {
    setPersonaOpen(false);
    setSelectedPersona(value);
  };

  const selectedPersonaObject = selectedPersona
    ? personas.find((p) => p.persona_id === selectedPersona)
    : null;

  // Sort bets based on sortBy option
  const sortedBets = React.useMemo(() => {
    if (!betsData?.data) return [];

    return [...betsData.data].sort((a, b) => {
      if (sortBy === "newest") {
        return (
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      } else {
        return (
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
      }
    });
  }, [betsData, sortBy]);

  if (isBetsError) {
    return (
      <div className="p-4 border border-red-800 bg-red-950/20 rounded-md text-red-400">
        ⚠️ Error loading betting data: {(betsError as Error).message}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header Section */}
      <div className="flex justify-between items-start">
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              setDashboardView(
                dashboardView === "standard" ? "analytics" : "standard",
              )
            }
            className="flex items-center gap-1"
          >
            {dashboardView === "standard" ? (
              <>
                <BarChart className="h-4 w-4" /> Analytics View
              </>
            ) : (
              <>
                <LayoutDashboard className="h-4 w-4" /> Standard View
              </>
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowBackfillModal(true)}
            className="flex items-center gap-1"
          >
            <Database className="h-4 w-4" /> Backfill
          </Button>
        </div>
      </div>

      {/* Persona selector */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div className="w-full sm:w-auto">
          <div className="w-full sm:w-auto flex flex-col gap-y-2">
            <Label>Agent</Label>
            <Popover open={personaOpen} onOpenChange={setPersonaOpen}>
              <PopoverTrigger asChild>
                <Button
                  id="persona-selector-betting"
                  data-testid="persona-selector-betting"
                  variant="outline"
                  role="combobox"
                  aria-expanded={personaOpen}
                  className="w-48 px-8 py-2 rounded-md"
                >
                  <span className="w-full sm:max-w-48 truncate">
                    {selectedPersona
                      ? personas.find(
                          (persona) => persona.persona_id === selectedPersona,
                        )?.name
                      : "All Agents"}
                  </span>
                  <ChevronsUpDown className="h-5 w-3 opacity-50 shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0">
                <Command>
                  <CommandInput placeholder="Search agent..." />
                  <CommandList>
                    <CommandEmpty>No agent found.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        key="all"
                        value="all"
                        onSelect={() => handlePersonaChange("")}
                        keywords={["all, none"]}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            !selectedPersona ? "opacity-100" : "opacity-0",
                          )}
                        />
                        All Agents
                      </CommandItem>
                      {personas
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((persona) => (
                          <CommandItem
                            key={persona.persona_id}
                            value={persona.persona_id}
                            onSelect={() =>
                              handlePersonaChange(persona.persona_id)
                            }
                            keywords={[persona.name]}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                selectedPersona === persona.persona_id
                                  ? "opacity-100"
                                  : "opacity-0",
                              )}
                            />
                            {persona.name}
                          </CommandItem>
                        ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest First</SelectItem>
              <SelectItem value="oldest">Oldest First</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex border border-zinc-800 rounded-md">
            <Button
              variant="ghost"
              size="sm"
              className={`px-2 ${viewMode === "cards" ? "bg-zinc-800" : ""}`}
              onClick={() => setViewMode("cards")}
            >
              <i className="ri-layout-grid-fill"></i>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={`px-2 ${viewMode === "table" ? "bg-zinc-800" : ""}`}
              onClick={() => setViewMode("table")}
            >
              <i className="ri-table-fill"></i>
            </Button>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isBetsLoading}
          >
            {isBetsLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Analytics View vs Standard View */}
      {dashboardView === "analytics" ? (
        // Analytics Dashboard View
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left column - BettingInsights */}
          <div className="lg:col-span-2">
            <BettingInsights
              bets={sortedBets}
              statsData={statsData}
              timeRange="30d"
            />
          </div>

          {/* Right column - Jobs Panel */}
          <div>
            <JobsPanel personaId={selectedPersona} limit={10} />
          </div>

          {/* Full width BettingStats */}
          <div className="lg:col-span-3">
            <BettingStats
              bets={sortedBets}
              personas={personas}
              statsData={statsData}
            />
          </div>

          {/* Full width Bets Display */}
          <div className="lg:col-span-3">
            <Card className="bg-black/60 backdrop-blur-sm overflow-hidden">
              <CardHeader className="p-3">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-base font-medium">
                    Bet History
                  </CardTitle>
                  <div className="flex items-center gap-1.5">
                    {selectedPersonaObject && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowValidateModal(true)}
                        className="flex items-center gap-1 h-7 bg-blue-950/30 border-blue-900 hover:bg-blue-900/40"
                      >
                        <Award className="h-3 w-3" /> Validate
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <div className="border-b border-zinc-800">
                <Tabs
                  defaultValue="all"
                  value={activeTab}
                  onValueChange={setActiveTab}
                >
                  <TabsList className="bg-transparent border-b border-zinc-800 p-0 h-auto rounded-none">
                    <TabsTrigger
                      value="all"
                      className="rounded-none data-[state=active]:bg-zinc-800 pb-2 pt-3 px-4"
                    >
                      All
                    </TabsTrigger>
                    <TabsTrigger
                      value="pending"
                      className="rounded-none data-[state=active]:bg-zinc-800 pb-2 pt-3 px-4"
                    >
                      Pending
                    </TabsTrigger>
                    <TabsTrigger
                      value="won"
                      className="rounded-none data-[state=active]:bg-zinc-800 pb-2 pt-3 px-4"
                    >
                      Won
                    </TabsTrigger>
                    <TabsTrigger
                      value="lost"
                      className="rounded-none data-[state=active]:bg-zinc-800 pb-2 pt-3 px-4"
                    >
                      Lost
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value={activeTab} className="p-0 m-0">
                    <BetsDisplay
                      bets={sortedBets}
                      personas={personas}
                      viewMode={viewMode}
                      isLoading={isBetsLoading}
                      selectedPersonaId={selectedPersona}
                      activeTab={activeTab}
                    />
                  </TabsContent>
                </Tabs>
              </div>
            </Card>
          </div>
        </div>
      ) : (
        // Standard Dashboard View
        <>
          {/* Dashboard metrics */}
          <BettingStats
            bets={betsData?.data || []}
            personas={personas}
            statsData={statsData}
          />

          {/* Bets Display Section and Jobs Panel */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-3">
              <Card className="bg-black/60 backdrop-blur-sm overflow-hidden">
                <CardHeader className="p-3">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-base font-medium">
                      Bet History
                    </CardTitle>
                    <div className="flex items-center gap-1.5">
                      {selectedPersonaObject && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowValidateModal(true)}
                          className="flex items-center gap-1 h-7 bg-blue-950/30 border-blue-900 hover:bg-blue-900/40"
                        >
                          <Award className="h-3 w-3" /> Validate
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <div className="border-b border-zinc-800">
                  <Tabs
                    defaultValue="pending"
                    value={activeTab}
                    onValueChange={setActiveTab}
                  >
                    <TabsList className="bg-transparent border-b border-zinc-800 p-0 h-auto rounded-none">
                      <TabsTrigger
                        value="pending"
                        className="rounded-none data-[state=active]:bg-zinc-800 pb-2 pt-3 px-4"
                      >
                        Pending
                      </TabsTrigger>
                      <TabsTrigger
                        value="won"
                        className="rounded-none data-[state=active]:bg-zinc-800 pb-2 pt-3 px-4"
                      >
                        Won
                      </TabsTrigger>
                      <TabsTrigger
                        value="lost"
                        className="rounded-none data-[state=active]:bg-zinc-800 pb-2 pt-3 px-4"
                      >
                        Lost
                      </TabsTrigger>
                      <TabsTrigger
                        value="all"
                        className="rounded-none data-[state=active]:bg-zinc-800 pb-2 pt-3 px-4"
                      >
                        All
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value={activeTab} className="p-0 m-0">
                      <BetsDisplay
                        bets={sortedBets}
                        personas={personas}
                        viewMode={viewMode}
                        isLoading={isBetsLoading}
                        selectedPersonaId={selectedPersona}
                        activeTab={activeTab}
                      />
                    </TabsContent>
                  </Tabs>
                </div>
              </Card>
            </div>

            <div>
              <JobsPanel personaId={selectedPersona} />
            </div>
          </div>
        </>
      )}

      {/* Modals */}
      {selectedPersonaObject && (
        <ValidateModal
          isOpen={showValidateModal}
          onClose={() => setShowValidateModal(false)}
          persona={selectedPersonaObject}
        />
      )}

      <BackfillModal
        isOpen={showBackfillModal}
        onClose={() => setShowBackfillModal(false)}
        personas={personas}
        initialPersonaId={selectedPersona}
      />
    </div>
  );
};

export default BettingDashboard;
